/* This component layout is base for subnavigation component so if this layout changes subnavigation should change too! */
import {
    Box,
    Button,
    Container,
    Flex,
    HStack,
    IconButton,
    Stack,
    Text,
    Divider,
    useBreakpointValue,
    useTheme,
    Link as ChakraLink
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getAssetUrl} from 'pwa-kit-react-sdk/ssr/universal/utils'
import React, {useEffect, useMemo, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    _360Icon,
    ARIcon,
    ArrowLeftRound2Icon,
    ArrowRightRound2Icon,
    EditIcon,
    PlayIcon,
    ZoomIcon,
    HistoryIcon
} from '../../../components/icons'
import ImageGallery from '../../../components/image-gallery'
import ImageGallerySkeleton from '../../../components/image-gallery/skeleton'
import StyledContent from '../../../components/styled-content'
import {MAIN_IMAGES_IMAGE_TYPE, CRO_EXPERIMENTS, HYBRID_CONTENT} from '../../../constants'
import {useProduct} from '../../../hooks'
import useElementScreenPosition from '../../../hooks/use-element-screen-position'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import PdpCta from './pdp-cta'
import CockpitVariations from './cockpit-variations'
import CockpitOrderSection from './cockpit-order-section'
import CockpitTags from './cockpit-tags'
import ProductSpecialOffers, {
    useProductSpecialOffers
} from '../../../components/special-offer/product-special-offers'
import useAR from '../../../hooks/use-ar'
import {EngravingModal, useEngravingModal} from '../../../hooks/use-engraving-modal'
import Certificates from './certificates'
import useProductCertificates from '../hooks/use-product-certificates'
import useMultiSite from '../../../hooks/use-multi-site'
import BVProductRating from '../../../components/bazaarvoice/rating'
import {useCroSlot, useCroVariantActive} from '../../../components/cro'
import HybridLink from '../../../components/hybrid-link'
import {customerServiceUrlBuilder} from '../../../utils/url'
import {getBVReviewProductIds} from '../../../utils/product-utils'
import {hasBVProductRating} from '../../../components/bazaarvoice/utils'

const Cockpit = ({
    product,
    promotions,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    showZoomModal,
    onHotspotClick,
    onMasterChange,
    onVariantChange,
    onRatingsClick,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const theme = useTheme()
    const {
        setRef: setHeadingRef,
        position: {top: headingTop},
        recalculatePosition
    } = useElementScreenPosition()
    // recalculate top positions when product changes
    useEffect(() => {
        recalculatePosition()
    }, [product, recalculatePosition])
    const {site} = useMultiSite()

    const {has3D, configurator3DLink, ARModel, videos, variationParams, hotspots, isEngravable} =
        useProduct(product)

    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const ARProps = useAR(ARModel)
    const engravingModal = useEngravingModal()
    const imagesContainerRef = useRef(null)
    const productSpecialOffers = useProductSpecialOffers(product, undefined, undefined, {
        isLoading: isPromotionsLoading || isVariantLoading,
        promotions
    })
    const cockpitVariationsRef = useRef(null)
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const mainGalleryImages = useMemo(
        () => [
            ...(product?.c_extraMainImage
                ? [{disBaseLink: product?.c_extraMainImage, link: product?.c_extraMainImage}]
                : []),
            ...(findImageGroupBy(product?.imageGroups, {
                viewType: MAIN_IMAGES_IMAGE_TYPE,
                selectedVariationAttributes: variationParams
            })?.images || []),
            ...(product?.c_extraMainImage2
                ? [{disBaseLink: product?.c_extraMainImage2, link: product?.c_extraMainImage2}]
                : []),
            ...(videos || [])
        ],
        [
            product?.imageGroups,
            variationParams,
            product?.c_extraMainImage,
            product?.c_extraMainImage2,
            videos
        ]
    )
    const mainHotspots = useMemo(() => hotspots?.[MAIN_IMAGES_IMAGE_TYPE], [hotspots])

    const stickyProps = {
        position: {lg: 'sticky'},
        top: {lg: `${headingTop}px`}
    }

    const sidebarSizeProps = {
        // figma is based on 12 column grid
        // this is the rough equivalent in chakra
        flexBasis: {
            lg: 'calc(25% + 0.5rem)',
            xl: 'calc(25% + 0.875rem)',
            '2xl': 'calc(25% + 0.75rem)'
        },
        // prevents children from stretching above the flex-basis size
        minWidth: 0
    }

    const onShowAllColorsClick = (isOpen) => {
        if (imagesContainerRef.current && !isOpen) {
            imagesContainerRef.current.scrollIntoView?.({block: 'start', behavior: 'smooth'})
        }
    }

    const certificates = useProductCertificates(product)

    useCroSlot(`master_product_${getBVReviewProductIds(product)}`)
    const cro039VariantActive = useCroVariantActive(
        CRO_EXPERIMENTS.CRO_039_CockpitCleanup.experimentId,
        CRO_EXPERIMENTS.CRO_039_CockpitCleanup.variantId
    )

    return (
        <>
            <Container layerStyle="page" variant="no-padding">
                {/* Headline */}
                {cro039VariantActive && !isDesktop && (
                    <Stack spacing={2} px={4} pb={4}>
                        <Flex direction="row" flexWrap="wrap" gap={4}>
                            {hasProductRating && (
                                <BVProductRating
                                    pid={getBVReviewProductIds(product)}
                                    variant="cockpit"
                                    onRatingsClick={onRatingsClick}
                                />
                            )}
                            <CockpitTags product={product} />
                        </Flex>

                        {/* Title */}
                        <Text as={'h1'} textStyle="Regular 2XLarge">
                            {product?.name}
                        </Text>
                    </Stack>
                )}

                <Flex
                    ref={setHeadingRef}
                    direction={{base: 'column', lg: 'row'}}
                    gap={{base: cro039VariantActive ? 8 : 6, '2xl': 8}}
                    alignItems={{lg: 'flex-start'}}
                >
                    {/* Product image - STOK-6322 */}
                    <Box ref={imagesContainerRef} flex={1} {...stickyProps}>
                        {!isVariantLoading ? (
                            <ImageGallery
                                variant="cockpit"
                                items={mainGalleryImages}
                                hotspots={mainHotspots}
                                cursor={`url('${encodeURI(
                                    getAssetUrl('static/ico/zoom.svg')
                                )}'),zoom-in`}
                                navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                                navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                                preload
                                showChildrenIn="overlay"
                                showPagination
                                imageProps={{
                                    widths: {base: '100vw', lg: '50vw'}
                                }}
                                onClick={
                                    showZoomModal &&
                                    ((_, image, index) => {
                                        showZoomModal({image, index})
                                    })
                                }
                                onHotspotClick={onHotspotClick}
                            >
                                <Flex justifyItems="space-between">
                                    <HStack spacing={2} pointerEvents="auto">
                                        {videos && videos?.length && (
                                            <IconButton
                                                variant="pillOnTile"
                                                aria-label={formatMessage({
                                                    id: 'product_detail.button.assistive_msg.video',
                                                    defaultMessage: 'Video'
                                                })}
                                                onClick={() => {
                                                    showZoomModal({tab: 1})
                                                }}
                                            >
                                                <PlayIcon boxSize={6} />
                                            </IconButton>
                                        )}

                                        {ARProps?.hasAR && (
                                            <>
                                                <IconButton
                                                    as={ChakraLink}
                                                    href={ARProps?.href || '#'}
                                                    rel="ar"
                                                    className="fest"
                                                    variant="pillOnTile"
                                                    aria-label={formatMessage({
                                                        id: 'product_detail.button.assistive_msg.ar',
                                                        defaultMessage: 'AR'
                                                    })}
                                                    onClick={ARProps?.onARLinkClick}
                                                >
                                                    <ARIcon boxSize={6} />
                                                </IconButton>
                                            </>
                                        )}
                                        {has3D && (
                                            <IconButton
                                                variant="pillOnTile"
                                                aria-label={formatMessage({
                                                    id: 'product_detail.button.assistive_msg.360view',
                                                    defaultMessage: '360 View'
                                                })}
                                                onClick={() => {
                                                    showZoomModal({tab: 2})
                                                }}
                                            >
                                                <_360Icon boxSize={6} />
                                            </IconButton>
                                        )}
                                    </HStack>
                                    <IconButton
                                        pointerEvents="auto"
                                        display={{md: 'none'}}
                                        marginLeft="auto"
                                        variant="pillOnTile"
                                        aria-label={formatMessage({
                                            id: 'product_detail.button.assistive_msg.zoom',
                                            defaultMessage: 'Zoom'
                                        })}
                                        onClick={() => {
                                            showZoomModal({tab: 0})
                                        }}
                                    >
                                        <ZoomIcon boxSize={6} />
                                    </IconButton>
                                </Flex>
                            </ImageGallery>
                        ) : (
                            <ImageGallerySkeleton />
                        )}
                    </Box>

                    {/* mobile top / desktop left column */}
                    <Stack
                        spacing={cro039VariantActive && !isDesktop ? 8 : 6}
                        order={{lg: -1}}
                        px={{base: 4, lg: 0}}
                        {...stickyProps}
                        {...sidebarSizeProps}
                    >
                        {/* Headline */}
                        {!cro039VariantActive || (cro039VariantActive && isDesktop) ? (
                            <Stack spacing={2}>
                                <Flex direction="row" flexWrap="wrap" gap={4}>
                                    {hasProductRating && (
                                        <BVProductRating
                                            pid={getBVReviewProductIds(product)}
                                            variant="cockpit"
                                            onRatingsClick={onRatingsClick}
                                        />
                                    )}
                                    <CockpitTags product={product} />
                                </Flex>

                                {/* Title */}
                                <Text as={'h1'} textStyle="Regular 2XLarge">
                                    {product?.name}
                                </Text>
                            </Stack>
                        ) : (
                            cro039VariantActive &&
                            !isDesktop && (
                                <>
                                    <Stack spacing={4} pb={2}>
                                        <CockpitVariations
                                            product={product}
                                            isMasterLoading={isMasterLoading}
                                            isVariantLoading={isVariantLoading}
                                            onMasterChange={onMasterChange}
                                            onVariantChange={onVariantChange}
                                            ARProps={ARProps}
                                            configurator3DLink={configurator3DLink}
                                            onShowAllColorsClick={onShowAllColorsClick}
                                            ref={cockpitVariationsRef}
                                            cro039VariantActive={cro039VariantActive}
                                        />

                                        {!site?.isContentOnlySite && isEngravable && (
                                            <Button
                                                variant={cro039VariantActive ? 'link' : 'outline'}
                                                size="lg"
                                                {...{
                                                    [cro039VariantActive
                                                        ? 'leftIcon'
                                                        : 'rightIcon']: <EditIcon boxSize={5} />
                                                }}
                                                alignSelf={cro039VariantActive && 'start'}
                                                onClick={engravingModal.onOpen}
                                            >
                                                <FormattedMessage
                                                    id="product_detail.button.engraving"
                                                    defaultMessage="Complimentary Engraving"
                                                />
                                            </Button>
                                        )}
                                    </Stack>

                                    {/* Bottom */}
                                    <CockpitOrderSection
                                        product={product}
                                        promotions={promotions}
                                        isPromotionsLoading={isPromotionsLoading}
                                        isVariantLoading={isVariantLoading}
                                        cro039VariantActive={cro039VariantActive}
                                        {...props}
                                    />

                                    <Divider colorScheme="stokkeGray" />
                                </>
                            )
                        )}

                        {/* highlights */}
                        <Stack spacing={2}>
                            <Text as={'h2'} textStyle="Semi Bold Small">
                                {product?.c_slogan}
                            </Text>
                            <StyledContent content={product?.c_USP} textStyle="List Regular" />
                        </Stack>

                        {cro039VariantActive && product?.c_pdpDescriptionContent ? (
                            <HybridLink
                                maxW="full"
                                page={HYBRID_CONTENT}
                                to={customerServiceUrlBuilder(product.c_pdpDescriptionContent)}
                            >
                                <Text textStyle="Regular Small">
                                    <FormattedMessage
                                        id="product_detail.link.siibs"
                                        defaultMessage="Important Ergonomic and Usage Information"
                                    />
                                </Text>
                            </HybridLink>
                        ) : null}

                        {product?.c_previousModelLink && (
                            <Flex gap={4} alignItems="center">
                                <HistoryIcon />
                                <StyledContent content={product.c_previousModelLink} />
                            </Flex>
                        )}

                        {/* Special offer on desktop */}
                        <Box display={{base: 'none', lg: 'block'}}>
                            {!isPromotionsLoading && (
                                <ProductSpecialOffers {...productSpecialOffers} />
                            )}
                        </Box>
                        {certificates?.length &&
                            (!cro039VariantActive || (cro039VariantActive && isDesktop)) && (
                                <Stack>
                                    <Text as={'h2'} textStyle="Semi Bold Small" paddingBottom={2}>
                                        <FormattedMessage
                                            id="product_view.header.awards_and_certificates"
                                            defaultMessage="Warranty & Certificates"
                                        />
                                    </Text>
                                    <Certificates certificates={certificates} />
                                </Stack>
                            )}
                    </Stack>

                    {/* mobile bottom / desktop right column */}
                    <Stack
                        spacing={cro039VariantActive && !isDesktop ? 8 : 6}
                        px={{base: 4, lg: 0}}
                        {...stickyProps}
                        {...sidebarSizeProps}
                        // figma design asks to put the box shadow to the whole container,
                        // but that shows it around all edges,
                        // instead apply the shadow only on the top edge
                        _before={{
                            // negate the padding to extend the shadow to the full width of the container
                            marginX: {base: -4, lg: 0},
                            // required to display the shadow
                            height: '1px',
                            // negate the height to not take up space
                            marginBottom: '-1px',
                            content: '""',
                            display: {base: 'block', lg: 'none'},
                            boxShadow: theme.shadows.top
                        }}
                    >
                        {/* Frame 3319 */}
                        {(!cro039VariantActive || (cro039VariantActive && isDesktop)) && (
                            <Stack spacing={6}>
                                <CockpitVariations
                                    product={product}
                                    isMasterLoading={isMasterLoading}
                                    isVariantLoading={isVariantLoading}
                                    onMasterChange={onMasterChange}
                                    onVariantChange={onVariantChange}
                                    ARProps={ARProps}
                                    configurator3DLink={configurator3DLink}
                                    onShowAllColorsClick={onShowAllColorsClick}
                                    ref={cockpitVariationsRef}
                                    cro039VariantActive={cro039VariantActive}
                                />

                                {!site?.isContentOnlySite && isEngravable && (
                                    <Button
                                        {...(cro039VariantActive
                                            ? {
                                                  leftIcon: <EditIcon boxSize={5} />,
                                                  alignSelf: 'start',
                                                  variant: 'link'
                                              }
                                            : {
                                                  rightIcon: <EditIcon boxSize={5} />,
                                                  variant: 'outline'
                                              })}
                                        size="lg"
                                        onClick={engravingModal.onOpen}
                                    >
                                        <FormattedMessage
                                            id="product_detail.button.engraving"
                                            defaultMessage="Complimentary Engraving"
                                        />
                                    </Button>
                                )}
                            </Stack>
                        )}

                        {/* Special offer on mobile */}
                        <Box display={{base: 'block', lg: 'none'}}>
                            {!isPromotionsLoading && (
                                <ProductSpecialOffers {...productSpecialOffers} />
                            )}
                        </Box>
                        {certificates?.length && cro039VariantActive && !isDesktop && (
                            <Stack>
                                <Text as={'h2'} textStyle="Semi Bold Small" paddingBottom={2}>
                                    <FormattedMessage
                                        id="product_view.header.awards_and_certificates"
                                        defaultMessage="Warranty & Certificates"
                                    />
                                </Text>
                                <Certificates certificates={certificates} />
                            </Stack>
                        )}

                        {/* Bottom */}
                        {(!cro039VariantActive || (cro039VariantActive && isDesktop)) && (
                            <CockpitOrderSection
                                product={product}
                                promotions={promotions}
                                isPromotionsLoading={isPromotionsLoading}
                                isVariantLoading={isVariantLoading}
                                cro039VariantActive={cro039VariantActive}
                                {...props}
                            />
                        )}
                    </Stack>
                    <PdpCta
                        isPromotionsLoading={isPromotionsLoading}
                        isVariantLoading={isVariantLoading}
                        product={product}
                        promotions={promotions}
                        {...props}
                    />
                </Flex>
            </Container>
            {isEngravable && (
                <EngravingModal
                    {...engravingModal}
                    isPromotionsLoading={isPromotionsLoading}
                    isVariantLoading={isVariantLoading}
                    product={product}
                    promotions={promotions}
                    onRecommendedProductClick={props.onRecommendedProductClick}
                />
            )}
        </>
    )
}

Cockpit.propTypes = {
    product: PropTypes.object,
    promotions: PropTypes.array,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    showZoomModal: PropTypes.func,
    notifyMe: PropTypes.func,
    onHotspotClick: PropTypes.func,
    onMasterChange: PropTypes.func,
    onVariantChange: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    onRatingsClick: PropTypes.func
}

export default Cockpit
